/**
 * useLegalAI Hook
 * React hook for interacting with the Legal AI service
 */

import { useState, useCallback, useRef } from 'react';
import {
  sendLegalAIChat,
  sendLegalAIChatStream,
  analyzeClause,
  improveClause,
  generateNegotiationAlternatives,
  executeQuickAction,
  checkLegalAIStatus,
  getConversations,
  getConversation,
  createConversation,
  addMessageToConversation,
  updateConversation as updateConversationApi,
  deleteConversation as deleteConversationApi,
  searchConversations as searchConversationsApi,
  LLMMode,
  AIProvider,
  ContractContext,
  ChatMessage,
  LegalAIChatResponse,
  ClauseAnalysisResponse,
  ClauseImprovementResponse,
  NegotiationAlternativesResponse,
  QuickAction,
  LegalAIStatus,
  ConversationSummary,
  StreamEvent,
  ConversationSearchResult,
  ConversationSearchResponse
} from '../services/legalAIService';

export interface UseLegalAIOptions {
  mode?: LLMMode;
  provider?: AIProvider;
  contractContext?: ContractContext;
  jurisdictionCode?: string;
  enableStreaming?: boolean; // Enable streaming responses by default
}

export interface UseLegalAIReturn {
  // State
  isLoading: boolean;
  isStreaming: boolean;
  streamingContent: string; // Current streaming content being built
  error: string | null;
  conversationHistory: ChatMessage[];
  lastResponse: LegalAIChatResponse | null;

  // Conversation persistence state
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  isLoadingConversations: boolean;

  // Search state
  searchResults: ConversationSearchResult[];
  isSearching: boolean;
  searchQuery: string;

  // Settings
  mode: LLMMode;
  provider: AIProvider | undefined;
  enableStreaming: boolean;

  // Actions
  sendMessage: (message: string, clauseText?: string) => Promise<LegalAIChatResponse>;
  setEnableStreaming: (enabled: boolean) => void;
  cancelStream: () => void;
  analyzeClauseAction: (clauseText: string, analysisType: 'risk' | 'clarity' | 'compliance' | 'negotiation' | 'simplify') => Promise<ClauseAnalysisResponse>;
  improveClauseAction: (clauseText: string, improvementType: 'clarity' | 'protection' | 'balance') => Promise<ClauseImprovementResponse>;
  getNegotiationAlternatives: (clauseText: string, position: 'strengthen' | 'soften' | 'balance') => Promise<NegotiationAlternativesResponse>;
  executeQuickActionAction: (action: QuickAction, clauseText: string) => Promise<LegalAIChatResponse>;

  // Configuration
  setMode: (mode: LLMMode) => void;
  setProvider: (provider: AIProvider | undefined) => void;
  setContractContext: (context: ContractContext | undefined) => void;
  setJurisdictionCode: (code: string | undefined) => void;

  // Conversation persistence actions
  loadConversations: (contractId: string) => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  startNewConversation: (contractId: string, model?: string, mode?: string) => Promise<string>;
  selectConversation: (conversationId: string) => Promise<void>;
  renameConversation: (conversationId: string, newTitle: string) => Promise<void>;
  archiveConversation: (conversationId: string) => Promise<void>;
  removeConversation: (conversationId: string) => Promise<void>;

  // Search actions
  searchConversations: (query: string, contractId?: string) => Promise<void>;
  clearSearch: () => void;

  // Utilities
  clearConversation: () => void;
  clearError: () => void;
  checkStatus: () => Promise<LegalAIStatus>;
}

export function useLegalAI(options: UseLegalAIOptions = {}): UseLegalAIReturn {
  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>([]);
  const [lastResponse, setLastResponse] = useState<LegalAIChatResponse | null>(null);

  // Conversation persistence state
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const currentContractIdRef = useRef<string | null>(null);

  // Search state
  const [searchResults, setSearchResults] = useState<ConversationSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Streaming control
  const [enableStreaming, setEnableStreamingState] = useState(options.enableStreaming ?? true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Settings (using refs to avoid re-renders on every settings change)
  const [mode, setModeState] = useState<LLMMode>(options.mode || 'standard');
  const [provider, setProviderState] = useState<AIProvider | undefined>(options.provider);
  const contractContextRef = useRef<ContractContext | undefined>(options.contractContext);
  const jurisdictionCodeRef = useRef<string | undefined>(options.jurisdictionCode);

  // Setters
  const setMode = useCallback((newMode: LLMMode) => {
    setModeState(newMode);
  }, []);

  const setProvider = useCallback((newProvider: AIProvider | undefined) => {
    setProviderState(newProvider);
  }, []);

  const setContractContext = useCallback((context: ContractContext | undefined) => {
    contractContextRef.current = context;
  }, []);

  const setJurisdictionCode = useCallback((code: string | undefined) => {
    jurisdictionCodeRef.current = code;
  }, []);

  // Load conversations for a contract
  const loadConversations = useCallback(async (contractId: string) => {
    setIsLoadingConversations(true);
    try {
      const convos = await getConversations(contractId);
      setConversations(convos);
      currentContractIdRef.current = contractId;
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  // Load a specific conversation with all messages
  const loadConversation = useCallback(async (conversationId: string) => {
    setIsLoading(true);
    try {
      const convo = await getConversation(conversationId);
      // Convert messages to ChatMessage format
      const messages: ChatMessage[] = convo.messages
        .filter(m => m.role !== 'system')
        .map(m => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: m.timestamp
        }));
      setConversationHistory(messages);
      setActiveConversationId(conversationId);
      setModeState(convo.aiMode as LLMMode);
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Start a new conversation
  const startNewConversation = useCallback(async (
    contractId: string,
    model?: string,
    conversationMode?: string
  ): Promise<string> => {
    try {
      const convo = await createConversation(contractId, undefined, model, conversationMode);
      setActiveConversationId(convo._id);
      setConversationHistory([]);
      setLastResponse(null);
      // Refresh conversation list
      await loadConversations(contractId);
      return convo._id;
    } catch (err) {
      console.error('Failed to create conversation:', err);
      setError(err instanceof Error ? err.message : 'Failed to create conversation');
      throw err;
    }
  }, [loadConversations]);

  // Select an existing conversation
  const selectConversation = useCallback(async (conversationId: string) => {
    await loadConversation(conversationId);
  }, [loadConversation]);

  // Rename a conversation
  const renameConversation = useCallback(async (conversationId: string, newTitle: string) => {
    try {
      await updateConversationApi(conversationId, { title: newTitle });
      // Update local state
      setConversations(prev =>
        prev.map(c => c._id === conversationId ? { ...c, title: newTitle } : c)
      );
    } catch (err) {
      console.error('Failed to rename conversation:', err);
      setError(err instanceof Error ? err.message : 'Failed to rename conversation');
    }
  }, []);

  // Archive a conversation
  const archiveConversation = useCallback(async (conversationId: string) => {
    try {
      await updateConversationApi(conversationId, { isArchived: true });
      // Remove from visible list
      setConversations(prev => prev.filter(c => c._id !== conversationId));
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
        setConversationHistory([]);
      }
    } catch (err) {
      console.error('Failed to archive conversation:', err);
      setError(err instanceof Error ? err.message : 'Failed to archive conversation');
    }
  }, [activeConversationId]);

  // Delete a conversation
  const removeConversation = useCallback(async (conversationId: string) => {
    try {
      await deleteConversationApi(conversationId);
      // Remove from list
      setConversations(prev => prev.filter(c => c._id !== conversationId));
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
        setConversationHistory([]);
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete conversation');
    }
  }, [activeConversationId]);

  // Search conversations
  const searchConversations = useCallback(async (query: string, contractId?: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setSearchQuery('');
      return;
    }

    setIsSearching(true);
    setSearchQuery(query);
    try {
      const response = await searchConversationsApi(query, { contractId });
      setSearchResults(response.results);
    } catch (err) {
      console.error('Failed to search conversations:', err);
      setError(err instanceof Error ? err.message : 'Failed to search conversations');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setSearchQuery('');
  }, []);

  // Streaming setters
  const setEnableStreaming = useCallback((enabled: boolean) => {
    setEnableStreamingState(enabled);
  }, []);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsStreaming(false);
      setStreamingContent('');
    }
  }, []);

  // Send a chat message (with persistence and optional streaming)
  const sendMessage = useCallback(async (message: string, clauseText?: string): Promise<LegalAIChatResponse> => {
    setIsLoading(true);
    setError(null);
    setStreamingContent('');

    // Add user message to history immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    setConversationHistory(prev => [...prev, userMessage]);

    try {
      let response: LegalAIChatResponse;

      if (enableStreaming) {
        // Use streaming
        setIsStreaming(true);
        let fullContent = '';
        let metadata: LegalAIChatResponse['metadata'] | null = null;

        const streamGenerator = sendLegalAIChatStream({
          message,
          mode,
          provider,
          contractContext: contractContextRef.current,
          conversationHistory,
          clauseText,
          jurisdictionCode: jurisdictionCodeRef.current
        });

        for await (const event of streamGenerator) {
          if (event.type === 'chunk') {
            fullContent += event.content;
            setStreamingContent(fullContent);
          } else if (event.type === 'done') {
            fullContent = event.content;
            metadata = event.metadata;
          } else if (event.type === 'error') {
            throw new Error(event.message);
          }
        }

        setIsStreaming(false);
        setStreamingContent('');

        response = {
          content: fullContent,
          metadata: metadata || {
            provider: provider || 'anthropic',
            model: 'claude-sonnet-5-5',
            mode,
            processingTimeMs: 0
          }
        };
      } else {
        // Use regular non-streaming
        response = await sendLegalAIChat({
          message,
          mode,
          provider,
          contractContext: contractContextRef.current,
          conversationHistory,
          clauseText,
          jurisdictionCode: jurisdictionCodeRef.current
        });
      }

      // Add assistant response to history
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.content,
        timestamp: new Date().toISOString()
      };

      // Update conversation history (replace streaming placeholder if any)
      setConversationHistory(prev => [...prev.filter(m => m !== userMessage), userMessage, assistantMessage]);
      setLastResponse(response);

      // Persist to database if we have an active conversation
      if (activeConversationId) {
        try {
          // Add user message
          await addMessageToConversation(activeConversationId, 'user', message);
          // Add assistant message
          await addMessageToConversation(activeConversationId, 'assistant', response.content, {
            model: response.metadata.model,
            mode: response.metadata.mode,
            processingTimeMs: response.metadata.processingTimeMs
          });
          // Refresh conversation list to update timestamps
          if (currentContractIdRef.current) {
            loadConversations(currentContractIdRef.current);
          }
        } catch (persistError) {
          console.error('Failed to persist message:', persistError);
          // Don't throw - the message was sent successfully
        }
      }

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      // Remove the user message if there was an error
      setConversationHistory(prev => prev.filter(m => m !== userMessage));
      throw err;
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingContent('');
    }
  }, [mode, provider, conversationHistory, activeConversationId, loadConversations, enableStreaming]);

  // Analyze a clause
  const analyzeClauseAction = useCallback(async (
    clauseText: string,
    analysisType: 'risk' | 'clarity' | 'compliance' | 'negotiation' | 'simplify'
  ): Promise<ClauseAnalysisResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await analyzeClause(
        clauseText,
        analysisType,
        contractContextRef.current,
        provider
      );
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze clause';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [provider]);

  // Improve a clause
  const improveClauseAction = useCallback(async (
    clauseText: string,
    improvementType: 'clarity' | 'protection' | 'balance'
  ): Promise<ClauseImprovementResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await improveClause(
        clauseText,
        improvementType,
        contractContextRef.current,
        provider
      );
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to improve clause';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [provider]);

  // Get negotiation alternatives
  const getNegotiationAlternatives = useCallback(async (
    clauseText: string,
    position: 'strengthen' | 'soften' | 'balance'
  ): Promise<NegotiationAlternativesResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await generateNegotiationAlternatives(
        clauseText,
        position,
        contractContextRef.current,
        provider
      );
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate alternatives';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [provider]);

  // Execute quick action
  const executeQuickActionAction = useCallback(async (
    action: QuickAction,
    clauseText: string
  ): Promise<LegalAIChatResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await executeQuickAction(
        action,
        clauseText,
        contractContextRef.current,
        provider
      );

      // Add to conversation history
      const userMessage: ChatMessage = {
        role: 'user',
        content: `[Quick Action: ${action}]\n\nClause:\n${clauseText}`,
        timestamp: new Date().toISOString()
      };

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.content,
        timestamp: new Date().toISOString()
      };

      setConversationHistory(prev => [...prev, userMessage, assistantMessage]);
      setLastResponse(response);

      // Persist to database if we have an active conversation
      if (activeConversationId) {
        try {
          await addMessageToConversation(activeConversationId, 'user', userMessage.content);
          await addMessageToConversation(activeConversationId, 'assistant', response.content, {
            model: response.metadata.model,
            mode: response.metadata.mode,
            processingTimeMs: response.metadata.processingTimeMs
          });
          if (currentContractIdRef.current) {
            loadConversations(currentContractIdRef.current);
          }
        } catch (persistError) {
          console.error('Failed to persist quick action:', persistError);
        }
      }

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to execute action';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [provider, activeConversationId, loadConversations]);

  // Clear conversation (local only, doesn't delete from database)
  const clearConversation = useCallback(() => {
    setConversationHistory([]);
    setLastResponse(null);
    setActiveConversationId(null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Check service status
  const checkStatus = useCallback(async (): Promise<LegalAIStatus> => {
    try {
      return await checkLegalAIStatus();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check status';
      setError(errorMessage);
      throw err;
    }
  }, []);

  return {
    // State
    isLoading,
    isStreaming,
    streamingContent,
    error,
    conversationHistory,
    lastResponse,

    // Conversation persistence state
    conversations,
    activeConversationId,
    isLoadingConversations,

    // Search state
    searchResults,
    isSearching,
    searchQuery,

    // Settings
    mode,
    provider,
    enableStreaming,

    // Actions
    sendMessage,
    setEnableStreaming,
    cancelStream,
    analyzeClauseAction,
    improveClauseAction,
    getNegotiationAlternatives,
    executeQuickActionAction,

    // Configuration
    setMode,
    setProvider,
    setContractContext,
    setJurisdictionCode,

    // Conversation persistence actions
    loadConversations,
    loadConversation,
    startNewConversation,
    selectConversation,
    renameConversation,
    archiveConversation,
    removeConversation,

    // Search actions
    searchConversations,
    clearSearch,

    // Utilities
    clearConversation,
    clearError,
    checkStatus
  };
}

export default useLegalAI;
